import { Check, CornerDownLeft, Loader2, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { type ReferenceData } from '@apps/web/components/phoebe_chat/assistant_response_types';
import {
    NEXT_ACTION_CHOICE_BUBBLE_CLASSES,
    WORKBENCH_USER_MESSAGE_BUBBLE_CLASSES,
} from '@apps/web/components/phoebe_chat/entity_visuals';
import {
    buildRecordedTraceEntriesFromResolved,
    buildRecordedTraceEntriesFromSubmission,
    displayResolvedAnswerText,
    getQuestionChoiceRows,
    getResolvedQuestionAnswers,
    getUserQuestions,
    isNextActionQuestionSet,
    type QuestionAnswerPayload,
    replaceReferenceTokensWithNames,
    type UserQuestionAnswerDraft,
    type UserQuestionItem,
    type UserQuestionResolvedAnswer,
} from '@apps/web/components/phoebe_chat/user_question_model';
import {
    processContentForMarkdown,
    renderReferenceAwareAnchor,
} from '@apps/web/components/phoebe_chat_view/assistant_responses/reference_markdown';
import { TeachPhoebeRecordedTrace } from '@apps/web/components/phoebe_chat_view/teach_phoebe_recorded_trace';
import { TeachPhoebeThankYou } from '@apps/web/components/phoebe_chat_view/teach_phoebe_thank_you';
import { type ToolCardPresentation } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';
import { useUserQuestionAnalytics } from '@apps/web/components/phoebe_chat_view/user_questions/use_user_question_analytics';
import { Button } from '@apps/web/components/ui/button';
import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import { Markdown } from '@apps/web/components/ui/markdown';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Textarea } from '@apps/web/components/ui/textarea';
import { cn } from '@apps/web/lib/utils';

const THANK_YOU_DURATION_MS = 2200;

// Shared shell so ask-user / next-steps / thank-you chips don't overflow the
// left (assistant) or right (user) chat column in narrow docked chat.
const TEACH_CARD_SHELL_CLASSES = 'w-full max-w-full min-w-0';

// Chosen next step becomes a right-aligned user message bubble.
function NextActionAnswerBubble({ text }: { readonly text: string }) {
    return (
        <div className={cn('flex justify-end', TEACH_CARD_SHELL_CLASSES)}>
            <div className={WORKBENCH_USER_MESSAGE_BUBBLE_CLASSES}>{text}</div>
        </div>
    );
}

function NextActionChoiceBubble({
    children,
    disabled,
    onClick,
}: {
    readonly children: React.ReactNode;
    readonly disabled: boolean;
    readonly onClick: () => void;
}) {
    return (
        <button
            type="button"
            className={NEXT_ACTION_CHOICE_BUBBLE_CLASSES}
            disabled={disabled}
            onClick={onClick}
        >
            {children}
        </button>
    );
}

function TeachPhoebeSproutIcon({ className }: { readonly className?: string }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden={true}
        >
            <path
                className="teach-phoebe-sprout-ground"
                pathLength={100}
                d="M5 21h14"
            />
            <path
                className="teach-phoebe-sprout-stem"
                pathLength={100}
                d="M14 9.536V7a4 4 0 0 1 4-4h1.5a.5.5 0 0 1 .5.5V5a4 4 0 0 1-4 4 4 4 0 0 0-4 4c0 2 1 3 1 5a5 5 0 0 1-1 3"
            />
            <path
                className="teach-phoebe-sprout-leaf"
                pathLength={100}
                d="M4 9a5 5 0 0 1 8 4 5 5 0 0 1-8-4"
            />
        </svg>
    );
}

function wrapTrainingRecordedTrace(
    trace: React.ReactNode,
    {
        nested,
        presentation,
    }: {
        readonly nested: boolean;
        readonly presentation: ToolCardPresentation;
    },
) {
    if (nested) {
        return trace;
    }
    if (presentation === 'minimal') {
        return (
            <div className="ml-7 flex w-[calc(100%-1.75rem)] max-w-full min-w-0 justify-start">
                {trace}
            </div>
        );
    }
    return (
        <div className={cn('flex justify-start', TEACH_CARD_SHELL_CLASSES)}>
            {trace}
        </div>
    );
}

function primaryQuestionActionLabel(
    compact: boolean,
    isLastQuestion: boolean,
): string {
    if (compact) {
        return isLastQuestion ? 'Save' : 'Continue';
    }
    return isLastQuestion ? 'Submit' : 'Next';
}

function QuestionReferenceMarkdown({
    content,
    references,
    className,
}: {
    readonly content: string;
    readonly references?: Array<ReferenceData>;
    readonly className?: string;
}) {
    const refMap = useMemo(
        () => new Map((references ?? []).map((r) => [r.id.toLowerCase(), r])),
        [references],
    );
    const processed = useMemo(
        () => processContentForMarkdown(content, refMap, new Map()),
        [content, refMap],
    );
    return (
        <Markdown
            className={className}
            components={{
                a: ({ href, children }) =>
                    renderReferenceAwareAnchor(href, children, refMap),
            }}
        >
            {processed}
        </Markdown>
    );
}

export function UserQuestionCard({
    entry,
    isSubmitting,
    onSubmit,
    onCancel,
    nested = false,
    resolved = false,
    presentation = 'default',
    showTrainingModeContext = false,
    trainingModeContextReady = true,
}: {
    entry: ToolExecutionEntry;
    isSubmitting: boolean;
    onSubmit?: (payload: QuestionAnswerPayload) => Promise<void>;
    onCancel?: () => Promise<void>;
    nested?: boolean;
    resolved?: boolean;
    presentation?: ToolCardPresentation;
    showTrainingModeContext?: boolean;
    // Whether the organization query behind training-mode context has
    // settled (data present, errored, or disabled by a null org id) — false
    // only during the brief genuinely-loading window, never permanently.
    // Defaults to true for callers that don't pass training-mode props
    // at all (admin/outreach surfaces), so this only defers rendering for
    // callers that opt into the training variant. See the render-time check
    // below for why this exists.
    trainingModeContextReady?: boolean;
}) {
    const questions = useMemo(() => getUserQuestions(entry), [entry]);
    const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
    const [draftAnswers, setDraftAnswers] = useState<
        Record<string, UserQuestionAnswerDraft>
    >({});
    const [skippedQuestionIds, setSkippedQuestionIds] = useState<
        Record<string, boolean>
    >({});
    const [otherSelectedQuestionIds, setOtherSelectedQuestionIds] = useState<
        Record<string, boolean>
    >({});
    const [otherDrafts, setOtherDrafts] = useState<Record<string, string>>({});
    const [openSearchableQuestionId, setOpenSearchableQuestionId] = useState<
        string | null
    >(null);
    const [choiceSearch, setChoiceSearch] = useState('');
    const canSubmit = !!onSubmit && !!entry.callId;
    const canCancel = !!onCancel && !!entry.callId && !isSubmitting;
    const { captureSubmitted, captureCancelled } = useUserQuestionAnalytics({
        entry,
        questions,
        actionable: canSubmit || canCancel,
        resolved,
        nested,
        presentation,
        showTrainingModeContext,
        trainingModeContextReady,
    });
    const activeQuestion =
        questions[Math.min(activeQuestionIndex, questions.length - 1)] ??
        questions[0];
    const resolvedAnswers = resolved
        ? getResolvedQuestionAnswers(entry, questions)
        : [];
    const activeQuestionRef = useRef<HTMLDivElement | null>(null);
    const questionCardRef = useRef<HTMLDivElement | null>(null);
    // Training mode shows a smaller, full-width card and lets the trainer drive
    // answer selection from the keyboard.
    const compact = showTrainingModeContext;
    // Which choice the arrow keys are pointing at (-1 = nothing yet).
    const [highlightedChoiceIndex, setHighlightedChoiceIndex] = useState(-1);
    // Set the moment an answer is submitted so the card is briefly replaced
    // by a simple acknowledgment before the resolved tool output removes it.
    const [showThankYou, setShowThankYou] = useState(false);
    // Training-mode next actions use the chosen label as their optimistic
    // user bubble while the persisted tool result catches up.
    const [submittedNextActionLabel, setSubmittedNextActionLabel] = useState<
        string | null
    >(null);
    const [pendingTraceEntries, setPendingTraceEntries] = useState<ReturnType<
        typeof buildRecordedTraceEntriesFromSubmission
    > | null>(null);
    // Standard-card receipt shown from the moment of submit until the
    // resolved tool output takes over rendering the identical receipt.
    const [pendingReceiptAnswers, setPendingReceiptAnswers] =
        useState<Array<UserQuestionResolvedAnswer> | null>(null);
    const thankYouTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    // Set when a searchable choice already handled an Enter, so the card's own
    // Enter branch does not act on the same keystroke.
    const suppressNextEnterRef = useRef(false);

    useEffect(() => {
        if (activeQuestionIndex >= questions.length) {
            setActiveQuestionIndex(Math.max(questions.length - 1, 0));
        }
    }, [activeQuestionIndex, questions.length]);

    // A later turn can reuse this component instance for a different set of
    // questions. Without this the new card opens on whichever question the
    // previous one was left on, which reads as the card jumping to a question
    // nobody asked for. The call id distinguishes successive single-question
    // calls whose generated fallback id is always `q1`, while remaining stable
    // when polling hands us a new object for the same call.
    const questionSetKey = useMemo(
        () =>
            [entry.callId ?? entry.commandId ?? entry.id]
                .concat(questions.map((question) => question.id))
                .join('|'),
        [entry.callId, entry.commandId, entry.id, questions],
    );
    useEffect(() => {
        if (thankYouTimeoutRef.current !== null) {
            clearTimeout(thankYouTimeoutRef.current);
            thankYouTimeoutRef.current = null;
        }
        setActiveQuestionIndex(0);
        setHighlightedChoiceIndex(-1);
        setShowThankYou(false);
        setSubmittedNextActionLabel(null);
        setPendingTraceEntries(null);
    }, [questionSetKey]);

    useEffect(() => {
        // scrollIntoView is not implemented in jsdom.
        activeQuestionRef.current?.scrollIntoView?.({
            block: 'nearest',
            behavior: 'smooth',
        });
    }, [activeQuestionIndex]);

    useEffect(() => {
        setHighlightedChoiceIndex(-1);
    }, [activeQuestionIndex]);

    useEffect(
        () => () => {
            if (thankYouTimeoutRef.current !== null) {
                clearTimeout(thankYouTimeoutRef.current);
            }
        },
        [],
    );

    const showTransientThankYou = (persistUntilResolved = false) => {
        if (thankYouTimeoutRef.current !== null) {
            clearTimeout(thankYouTimeoutRef.current);
        }
        setShowThankYou(true);
        if (persistUntilResolved) {
            return;
        }
        thankYouTimeoutRef.current = setTimeout(() => {
            setShowThankYou(false);
            thankYouTimeoutRef.current = null;
        }, THANK_YOU_DURATION_MS);
    };

    const activeInputType = activeQuestion?.inputType;

    useEffect(() => {
        // In training mode, focus the card so arrow keys drive selection right
        // away. Text questions keep native textarea focus for typing.
        //
        // Also gated on trainingModeContextReady: the card renders nothing
        // until readiness is known (see the render-time check below), so
        // this is belt-and-suspenders — but it keeps the guard explicit
        // here rather than relying on the caller's variant flag happening
        // to be false too.
        if (!trainingModeContextReady || !showTrainingModeContext || resolved) {
            return;
        }
        if (!activeInputType || activeInputType === 'text') {
            return;
        }
        // The composer isn't blocked while a question is pending, so this
        // card can mount mid-typing. Don't steal focus from another field —
        // its keydown handler only understands arrows/Space/Enter, so
        // printable keystrokes meant for the composer would be dropped.
        const activeElement = document.activeElement;
        const isTypingElsewhere =
            activeElement instanceof HTMLElement &&
            activeElement !== document.body &&
            !questionCardRef.current?.contains(activeElement) &&
            (activeElement.tagName === 'TEXTAREA' ||
                activeElement.tagName === 'INPUT' ||
                activeElement.isContentEditable);
        if (isTypingElsewhere) {
            return;
        }
        questionCardRef.current?.focus?.();
    }, [
        trainingModeContextReady,
        showTrainingModeContext,
        resolved,
        activeQuestionIndex,
        activeInputType,
    ]);

    const setAnswer = (
        question: UserQuestionItem,
        answer: UserQuestionAnswerDraft,
    ) => {
        setSkippedQuestionIds((current) => {
            const next = { ...current };
            delete next[question.id];
            return next;
        });
        setDraftAnswers((current) => ({
            ...current,
            [question.id]: answer,
        }));
    };

    // Selecting Other… on a choice question reveals a free-text input;
    // single_choice replaces the picked choice, multi_choice toggles an
    // extra entry alongside the picked choices.
    const selectOther = (question: UserQuestionItem) => {
        setSkippedQuestionIds((current) => {
            const next = { ...current };
            delete next[question.id];
            return next;
        });
        if (question.inputType === 'single_choice') {
            setDraftAnswers((current) => {
                const next = { ...current };
                delete next[question.id];
                return next;
            });
            setOtherSelectedQuestionIds((current) => ({
                ...current,
                [question.id]: true,
            }));
            return;
        }
        setOtherSelectedQuestionIds((current) => ({
            ...current,
            [question.id]: !current[question.id],
        }));
    };

    const answerForQuestion = (
        question: UserQuestionItem,
    ): string | Array<string> | null => {
        const answer = draftAnswers[question.id];
        const otherSelected = otherSelectedQuestionIds[question.id] === true;
        const otherText = otherSelected
            ? (otherDrafts[question.id] ?? '').trim()
            : '';
        if (question.inputType === 'text') {
            return typeof answer === 'string' && answer.trim()
                ? answer.trim()
                : null;
        }
        if (question.inputType === 'multi_choice') {
            // An Other… selection with an empty draft blocks submission.
            if (otherSelected && !otherText) {
                return null;
            }
            const picked = Array.isArray(answer) ? answer : [];
            const combined = otherText ? [...picked, otherText] : picked;
            return combined.length > 0 ? combined : null;
        }
        if (question.inputType === 'single_choice' && otherSelected) {
            return otherText ? otherText : null;
        }
        return typeof answer === 'string' && answer ? answer : null;
    };

    const submitAnswers = async (skipQuestionId: string | null = null) => {
        if (!onSubmit || !entry.callId) {
            return;
        }

        const answers = questions.map((question) => {
            const answer = answerForQuestion(question);
            const skipped =
                skipQuestionId === question.id ||
                skippedQuestionIds[question.id] === true ||
                answer === null;
            if (skipped) {
                return {
                    question_id: question.id,
                    answer: null,
                    skipped: true,
                };
            }
            return {
                question_id: question.id,
                answer,
                skipped: false,
            };
        });
        const payload =
            questions.length === 1 && answers[0]?.skipped !== true
                ? { answer: answers[0]?.answer ?? null }
                : { answers };
        const isNextAction = isNextActionQuestionSet(questions);
        const isTrainingAskUserTrace = showTrainingModeContext && !isNextAction;
        if (isTrainingAskUserTrace) {
            setPendingTraceEntries(
                buildRecordedTraceEntriesFromSubmission(
                    questions,
                    entry,
                    answers,
                ),
            );
        }
        // The standard card settles straight into its receipt at submit time,
        // built from the local answers, so the transition is immediate and
        // the later resolved output swaps in an identical card.
        const showsReceiptPreview = !showTrainingModeContext && !isNextAction;
        if (showsReceiptPreview) {
            setPendingReceiptAnswers(
                answers.map((answer) => ({
                    questionId: answer.question_id,
                    question:
                        questions.find(
                            (question) => question.id === answer.question_id,
                        )?.question ?? 'Phoebe asked a question.',
                    answerText: answer.skipped
                        ? 'Skipped'
                        : Array.isArray(answer.answer)
                          ? answer.answer.join(', ')
                          : (answer.answer ?? ''),
                    skipped: answer.skipped,
                })),
            );
        }
        showTransientThankYou(isTrainingAskUserTrace || showsReceiptPreview);
        try {
            await onSubmit(payload);
            const skippedCount = answers.filter(
                (answer) => answer.skipped,
            ).length;
            captureSubmitted({
                answeredCount: answers.length - skippedCount,
                skippedCount,
            });
        } catch {
            // The answer handler owns the user-facing error toast. Restore
            // the card so the user can answer again.
            if (thankYouTimeoutRef.current !== null) {
                clearTimeout(thankYouTimeoutRef.current);
                thankYouTimeoutRef.current = null;
            }
            setShowThankYou(false);
            setPendingTraceEntries(null);
            setPendingReceiptAnswers(null);
        }
    };

    // Next-action choices submit on click — one tap, no separate Submit.
    // The chosen label is kept so the answered card can render as a
    // scheduler text-message bubble instead of an "Answer recorded" chip.
    const submitNextAction = async (value: string, label: string) => {
        if (!onSubmit || !entry.callId) {
            return;
        }
        setSubmittedNextActionLabel(label);
        // Keep the recorded choice in place until the persisted tool result
        // arrives. Restoring actionable CTAs on a slow poll would invite a
        // duplicate submission.
        showTransientThankYou(true);
        try {
            await onSubmit({ answer: value });
            captureSubmitted({ answeredCount: 1, skippedCount: 0 });
        } catch {
            // The answer handler owns the user-facing error toast. Restore
            // the card so the user can choose again.
            if (thankYouTimeoutRef.current !== null) {
                clearTimeout(thankYouTimeoutRef.current);
                thankYouTimeoutRef.current = null;
            }
            setShowThankYou(false);
            setSubmittedNextActionLabel(null);
        }
    };

    const cancelQuestion = async () => {
        if (!onCancel || !entry.callId) {
            return;
        }
        try {
            await onCancel();
            captureCancelled();
        } catch {
            // The answer handler owns the user-facing error toast.
        }
    };

    /**
     * Move past a question the moment it is answered by a single click.
     *
     * Deliberately takes no state: the answer that triggered it was set in the
     * same event, and `draftAnswers` does not carry it until the next render —
     * so a version that re-read the draft to check "is this answered" would
     * see nothing and silently do nothing. That stale read is what made
     * clicking feel unresponsive.
     *
     * The last question does not advance: submitting is the reader's call, not
     * a side effect of picking an option.
     */
    const advanceAfterSingleAnswer = (question: UserQuestionItem) => {
        if (question.id !== activeQuestion?.id) {
            return;
        }
        if (activeQuestionIndex >= questions.length - 1) {
            return;
        }
        setActiveQuestionIndex((current) =>
            Math.min(current + 1, questions.length - 1),
        );
    };

    const goToNextQuestion = async () => {
        if (!activeQuestion || answerForQuestion(activeQuestion) === null) {
            return;
        }
        if (activeQuestionIndex >= questions.length - 1) {
            await submitAnswers();
            return;
        }
        setActiveQuestionIndex((current) =>
            Math.min(current + 1, questions.length - 1),
        );
    };

    const skipQuestion = async () => {
        if (!activeQuestion) {
            return;
        }
        setSkippedQuestionIds((current) => ({
            ...current,
            [activeQuestion.id]: true,
        }));
        if (activeQuestionIndex >= questions.length - 1) {
            await submitAnswers(activeQuestion.id);
            return;
        }
        setActiveQuestionIndex((current) =>
            Math.min(current + 1, questions.length - 1),
        );
    };

    const toggleMultiChoice = (question: UserQuestionItem, choice: string) => {
        const currentValue = draftAnswers[question.id];
        const current = Array.isArray(currentValue) ? currentValue : [];
        setAnswer(
            question,
            current.includes(choice)
                ? current.filter((value) => value !== choice)
                : [...current, choice],
        );
    };

    const renderChoiceRows = (
        question: UserQuestionItem,
        {
            muted,
            disabled,
            teachStyle = false,
        }: {
            readonly muted: boolean;
            readonly disabled: boolean;
            readonly teachStyle?: boolean;
        },
    ) => {
        const currentValue = draftAnswers[question.id];
        const selectedValues = Array.isArray(currentValue)
            ? currentValue
            : typeof currentValue === 'string'
              ? [currentValue]
              : [];
        const choiceRows = getQuestionChoiceRows(question);
        const supportsOther =
            question.allowOther &&
            (question.inputType === 'single_choice' ||
                question.inputType === 'multi_choice');
        const otherSelected =
            supportsOther && otherSelectedQuestionIds[question.id] === true;
        // Only the active (non-disabled) question shows the keyboard highlight.
        const highlightActive = showTrainingModeContext && !disabled;

        const renderTeachChoiceButton = (
            label: React.ReactNode,
            {
                selected,
                highlighted,
                onClick,
            }: {
                readonly selected: boolean;
                readonly highlighted: boolean;
                readonly onClick: () => void;
            },
        ) => (
            <button
                type="button"
                disabled={disabled || isSubmitting || !canSubmit}
                onClick={onClick}
                className={cn(
                    'flex w-full min-w-0 items-center justify-between gap-3 rounded-[10px] px-3 py-2.5 text-left text-sm transition-colors',
                    disabled
                        ? 'cursor-default'
                        : 'hover:bg-muted/60 cursor-pointer',
                    selected || highlighted
                        ? 'bg-training-accent/10 text-training-accent-strong font-medium'
                        : 'text-foreground',
                    muted && !selected && !highlighted
                        ? 'text-muted-foreground'
                        : null,
                )}
            >
                <span className="min-w-0 flex-1 break-words">{label}</span>
                <Check
                    className={cn(
                        'text-training-accent h-4 w-4 shrink-0 transition-opacity',
                        selected ? 'opacity-100' : 'opacity-0',
                    )}
                    aria-hidden={true}
                />
            </button>
        );

        if (teachStyle) {
            return (
                <div className="flex flex-col gap-1">
                    {choiceRows.map((choice, index) => {
                        const selected = selectedValues.includes(choice.value);
                        const highlighted =
                            highlightActive && highlightedChoiceIndex === index;
                        return (
                            <React.Fragment key={choice.value}>
                                {renderTeachChoiceButton(
                                    replaceReferenceTokensWithNames(
                                        choice.label,
                                        entry.references,
                                    ),
                                    {
                                        selected,
                                        highlighted,
                                        onClick: () => {
                                            if (
                                                question.inputType ===
                                                'multi_choice'
                                            ) {
                                                toggleMultiChoice(
                                                    question,
                                                    choice.value,
                                                );
                                            } else {
                                                if (
                                                    question.inputType ===
                                                    'single_choice'
                                                ) {
                                                    setOtherSelectedQuestionIds(
                                                        (current) => {
                                                            const next = {
                                                                ...current,
                                                            };
                                                            delete next[
                                                                question.id
                                                            ];
                                                            return next;
                                                        },
                                                    );
                                                }
                                                setAnswer(
                                                    question,
                                                    choice.value,
                                                );
                                            }
                                        },
                                    },
                                )}
                            </React.Fragment>
                        );
                    })}
                    {supportsOther
                        ? renderTeachChoiceButton('Other…', {
                              selected: otherSelected,
                              highlighted:
                                  highlightActive &&
                                  highlightedChoiceIndex === choiceRows.length,
                              onClick: () => selectOther(question),
                          })
                        : null}
                    {otherSelected ? (
                        <Textarea
                            value={otherDrafts[question.id] ?? ''}
                            onChange={(event) => {
                                const value = event.currentTarget.value;
                                setOtherDrafts((current) => ({
                                    ...current,
                                    [question.id]: value,
                                }));
                            }}
                            placeholder={
                                question.placeholder ?? 'Type your own answer'
                            }
                            disabled={disabled || isSubmitting || !canSubmit}
                            className="min-h-12"
                        />
                    ) : null}
                </div>
            );
        }

        // Standard (non-training) rows: quiet full-width rows with a trailing
        // check on selection. Training mode always takes the teachStyle branch
        // above, so this branch never renders compact.
        return (
            <div className="space-y-0.5">
                {choiceRows.map((choice) => {
                    const selected = selectedValues.includes(choice.value);
                    return (
                        <button
                            key={choice.value}
                            type="button"
                            disabled={disabled || isSubmitting || !canSubmit}
                            onClick={() => {
                                if (question.inputType === 'multi_choice') {
                                    toggleMultiChoice(question, choice.value);
                                    return;
                                }
                                if (question.inputType === 'single_choice') {
                                    setOtherSelectedQuestionIds((current) => {
                                        const next = { ...current };
                                        delete next[question.id];
                                        return next;
                                    });
                                }
                                setAnswer(question, choice.value);
                                // One click answers a single-answer question,
                                // so it should not also cost a click on Next.
                                advanceAfterSingleAnswer(question);
                            }}
                            className={cn(
                                'flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors',
                                disabled
                                    ? 'cursor-default'
                                    : 'hover:bg-muted/60 cursor-pointer',
                                selected ? 'bg-muted font-medium' : null,
                                muted
                                    ? 'text-muted-foreground'
                                    : 'text-foreground',
                            )}
                        >
                            <span className="min-w-0 flex-1 break-words">
                                {replaceReferenceTokensWithNames(
                                    choice.label,
                                    entry.references,
                                )}
                            </span>
                            <Check
                                className={cn(
                                    'h-4 w-4 shrink-0',
                                    selected ? 'opacity-100' : 'opacity-0',
                                )}
                                aria-hidden={true}
                            />
                        </button>
                    );
                })}
                {supportsOther ? (
                    otherSelected ? (
                        // The Other row becomes the input in place: type your
                        // answer inside the selected row itself. The trailing
                        // check toggles it back off on multi-select.
                        <div className="bg-muted flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-sm">
                            <input
                                // Selecting Other is an explicit request to
                                // type here, so focus moves into the row.
                                autoFocus={true}
                                type="text"
                                value={otherDrafts[question.id] ?? ''}
                                onChange={(event) => {
                                    const value = event.currentTarget.value;
                                    setOtherDrafts((current) => ({
                                        ...current,
                                        [question.id]: value,
                                    }));
                                }}
                                placeholder={
                                    question.placeholder ??
                                    'Type your own answer'
                                }
                                disabled={
                                    disabled || isSubmitting || !canSubmit
                                }
                                className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent font-medium outline-none"
                            />
                            {question.inputType === 'multi_choice' ? (
                                <button
                                    type="button"
                                    aria-label="Remove your own answer"
                                    disabled={
                                        disabled || isSubmitting || !canSubmit
                                    }
                                    onClick={() => selectOther(question)}
                                    className="cursor-pointer"
                                >
                                    <Check className="h-4 w-4 shrink-0" />
                                </button>
                            ) : (
                                <Check
                                    className="h-4 w-4 shrink-0"
                                    aria-hidden={true}
                                />
                            )}
                        </div>
                    ) : (
                        <button
                            type="button"
                            disabled={disabled || isSubmitting || !canSubmit}
                            onClick={() => selectOther(question)}
                            className={cn(
                                'text-muted-foreground flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors',
                                disabled
                                    ? 'cursor-default'
                                    : 'hover:bg-muted/60 cursor-pointer',
                            )}
                        >
                            <span className="min-w-0 flex-1 break-words">
                                Other…
                            </span>
                            <Check
                                className="h-4 w-4 shrink-0 opacity-0"
                                aria-hidden={true}
                            />
                        </button>
                    )
                ) : null}
            </div>
        );
    };

    const renderSearchableSingleChoice = (
        question: UserQuestionItem,
        {
            muted,
            disabled,
        }: { readonly muted: boolean; readonly disabled: boolean },
    ) => {
        const choiceRows = getQuestionChoiceRows(question);
        const currentValue = draftAnswers[question.id];
        const selectedChoice = choiceRows.find(
            (choice) => choice.value === currentValue,
        );
        const otherSelected =
            question.allowOther &&
            otherSelectedQuestionIds[question.id] === true;
        const clearOtherSelection = () => {
            setOtherSelectedQuestionIds((current) => {
                const next = { ...current };
                delete next[question.id];
                return next;
            });
        };

        return (
            <div className="space-y-2">
                <Combobox
                    open={openSearchableQuestionId === question.id}
                    onOpenChange={(open) => {
                        setOpenSearchableQuestionId(open ? question.id : null);
                    }}
                    displayValue={
                        selectedChoice
                            ? replaceReferenceTokensWithNames(
                                  selectedChoice.label,
                                  entry.references,
                              )
                            : otherSelected
                              ? 'Other…'
                              : undefined
                    }
                    placeholder={question.placeholder ?? 'Select an option'}
                    disabled={disabled || isSubmitting || !canSubmit}
                    searchValue={choiceSearch}
                    onSearchValueChange={setChoiceSearch}
                    searchPlaceholder="Search options…"
                    triggerClassName={cn(
                        'text-left overflow-hidden',
                        muted && 'text-muted-foreground',
                    )}
                    contentClassName="max-w-[calc(100vw-2rem)] p-1"
                    commandListProps={{ className: 'max-h-64' }}
                >
                    <CommandEmpty className="text-muted-foreground py-6 text-center text-xs">
                        No matching options
                    </CommandEmpty>
                    {choiceRows.map((choice) => (
                        <CommandItem
                            key={choice.value}
                            value={replaceReferenceTokensWithNames(
                                choice.label,
                                entry.references,
                            )}
                            onSelect={() => {
                                clearOtherSelection();
                                setAnswer(question, choice.value);
                                setOpenSearchableQuestionId(null);
                                advanceAfterSingleAnswer(question);
                                // cmdk's Enter handler does not stop
                                // propagation, so the same key reaches the
                                // card's own Enter branch and would advance a
                                // second time, skipping a question.
                                suppressNextEnterRef.current = true;
                            }}
                            className="py-2 text-left text-sm"
                        >
                            {replaceReferenceTokensWithNames(
                                choice.label,
                                entry.references,
                            )}
                        </CommandItem>
                    ))}
                    {question.allowOther ? (
                        <CommandItem
                            value="Other"
                            onSelect={() => {
                                selectOther(question);
                                setOpenSearchableQuestionId(null);
                            }}
                            className="py-2 text-left text-sm"
                        >
                            Other…
                        </CommandItem>
                    ) : null}
                </Combobox>
                {otherSelected ? (
                    <Textarea
                        value={otherDrafts[question.id] ?? ''}
                        onChange={(event) => {
                            const value = event.currentTarget.value;
                            setOtherDrafts((current) => ({
                                ...current,
                                [question.id]: value,
                            }));
                        }}
                        placeholder={
                            question.placeholder ?? 'Type your own answer'
                        }
                        disabled={disabled || isSubmitting || !canSubmit}
                        className={compact ? 'min-h-12' : 'min-h-16'}
                    />
                ) : null}
            </div>
        );
    };

    const canAdvance =
        !!activeQuestion &&
        !isSubmitting &&
        canSubmit &&
        answerForQuestion(activeQuestion) !== null;

    const selectHighlightedChoice = (
        question: UserQuestionItem,
        choiceRows: ReturnType<typeof getQuestionChoiceRows>,
        index: number,
    ) => {
        const choice = choiceRows[index];
        if (!choice) {
            selectOther(question);
            return;
        }
        if (question.inputType === 'multi_choice') {
            toggleMultiChoice(question, choice.value);
            return;
        }
        // single_choice: picking a listed choice clears any prior Other….
        setOtherSelectedQuestionIds((current) => {
            const next = { ...current };
            delete next[question.id];
            return next;
        });
        setAnswer(question, choice.value);
        // Committing from the keyboard answers the question exactly as a click
        // does, so it advances the same way — otherwise a keyboard trainer
        // spends an extra keystroke per question.
        advanceAfterSingleAnswer(question);
    };

    // Plain Enter advances (or submits on the last question) once the active
    // question is answered; Shift+Enter falls through so textareas keep their
    // newline behavior. In training mode, arrow keys move a choice highlight
    // and Space toggles it.
    const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (resolved) {
            return;
        }
        // Training mode: arrow keys move a highlight through the choices, and
        // single-selection questions stage the highlighted answer as it moves
        // (radio style). Textareas keep native caret movement. Skip while a
        // submission is in flight so keys can't mutate the draft mid-submit.
        const target = event.target as HTMLElement;
        const inTextField =
            target.tagName === 'TEXTAREA' || target.tagName === 'INPUT';
        if (
            showTrainingModeContext &&
            !inTextField &&
            canSubmit &&
            !isSubmitting &&
            activeQuestion &&
            activeQuestion.inputType !== 'text' &&
            !activeQuestion.searchable
        ) {
            const choiceRows = getQuestionChoiceRows(activeQuestion);
            const supportsOther =
                activeQuestion.allowOther &&
                (activeQuestion.inputType === 'single_choice' ||
                    activeQuestion.inputType === 'multi_choice');
            const rowCount = choiceRows.length + (supportsOther ? 1 : 0);
            if (
                rowCount > 0 &&
                (event.key === 'ArrowDown' || event.key === 'ArrowUp')
            ) {
                event.preventDefault();
                const delta = event.key === 'ArrowDown' ? 1 : -1;
                const nextIndex =
                    highlightedChoiceIndex < 0
                        ? 0
                        : Math.min(
                              Math.max(highlightedChoiceIndex + delta, 0),
                              rowCount - 1,
                          );
                // Moving the highlight never answers the question. The card
                // takes focus on mount, so staging as the highlight moved meant
                // one ArrowDown — scrolling the panel, say — silently answered
                // A, and holding it walked the highlight onto the Other row and
                // staged Other. Space, Enter, or a click commits; nothing else
                // does.
                setHighlightedChoiceIndex(nextIndex);
                return;
            }
            // Space toggles the highlighted choice (needed for multi_choice);
            // only when the card itself is focused, so it never double-fires a
            // focused choice button's own activation.
            if (
                event.key === ' ' &&
                event.target === event.currentTarget &&
                highlightedChoiceIndex >= 0
            ) {
                event.preventDefault();
                selectHighlightedChoice(
                    activeQuestion,
                    choiceRows,
                    highlightedChoiceIndex,
                );
                return;
            }
        }

        // Plain Enter advances (or submits on the last question).
        if (event.key !== 'Enter' || event.shiftKey) {
            return;
        }
        if (suppressNextEnterRef.current) {
            suppressNextEnterRef.current = false;
            return;
        }
        if (!canAdvance) {
            return;
        }
        event.preventDefault();
        void goToNextQuestion();
    };

    // Defer the first paint until training-mode readiness is known, so this
    // card never renders the wrong variant and then snaps to the right one.
    // All hooks above have already run unconditionally; this is a plain
    // render-time bail, not a conditional hook.
    if (!trainingModeContextReady) {
        return null;
    }

    const showRecordedState = showThankYou && !resolved;
    // The "What do you want next?" card: choices are next steps Phoebe
    // carries out, rendered as one-click action buttons. Training mode only
    // (the doctrine only raises next_action questions there); outside it the
    // question falls back to the standard card.
    const nextAction =
        showTrainingModeContext && isNextActionQuestionSet(questions);
    const trainingAskUser = compact && !nextAction;
    const trainingTraceEntries = trainingAskUser
        ? resolved
            ? buildRecordedTraceEntriesFromResolved(
                  questions,
                  entry,
                  resolvedAnswers,
              )
            : showRecordedState && pendingTraceEntries
              ? pendingTraceEntries
              : null
        : null;

    if (trainingTraceEntries) {
        const answeredCount = trainingTraceEntries.filter(
            (item) => !item.skipped,
        ).length;
        return wrapTrainingRecordedTrace(
            <TeachPhoebeRecordedTrace
                entries={trainingTraceEntries}
                meta={
                    trainingTraceEntries.length > 1
                        ? `${answeredCount} answer${answeredCount === 1 ? '' : 's'}`
                        : null
                }
            />,
            { nested, presentation },
        );
    }

    // Answered training next-action questions show the chosen step as a
    // scheduler message bubble; skips and cancels fall through to the chip.
    if (resolved && compact) {
        // An answered next-steps card shows the chosen action as a scheduler
        // message bubble; skips and cancels fall through to the plain chip.
        if (nextAction) {
            const answer = resolvedAnswers[0];
            if (
                answer &&
                !answer.skipped &&
                answer.answerText !== 'Cancelled'
            ) {
                return (
                    <NextActionAnswerBubble
                        text={replaceReferenceTokensWithNames(
                            displayResolvedAnswerText(answer, questions[0]),
                            entry.references,
                        )}
                    />
                );
            }
        }
        const answeredCount = resolvedAnswers.filter(
            (answer) => !answer.skipped,
        ).length;
        const chip = (
            <TeachPhoebeThankYou
                showSprout={true}
                variant="chip"
                label={
                    answeredCount === 0
                        ? 'Question skipped'
                        : answeredCount === 1
                          ? 'Answer recorded'
                          : 'Answers recorded'
                }
            />
        );
        if (nested) {
            return chip;
        }
        if (presentation === 'minimal') {
            return (
                <div className="ml-7 flex w-[calc(100%-1.75rem)] max-w-full min-w-0 justify-start">
                    {chip}
                </div>
            );
        }
        return (
            <div className={cn('flex justify-start', TEACH_CARD_SHELL_CLASSES)}>
                {chip}
            </div>
        );
    }

    const activeOtherSelected =
        !!activeQuestion &&
        otherSelectedQuestionIds[activeQuestion.id] === true;
    const activeOtherText = activeQuestion
        ? (otherDrafts[activeQuestion.id] ?? '').trim()
        : '';
    const nextActionCard =
        nextAction && activeQuestion ? (
            <div
                data-testid="user-question-card"
                className={cn(
                    'relative flex flex-col gap-2 break-words',
                    TEACH_CARD_SHELL_CLASSES,
                    showRecordedState && 'gap-0',
                )}
            >
                {showRecordedState ? (
                    <NextActionAnswerBubble
                        text={submittedNextActionLabel ?? 'On it'}
                    />
                ) : (
                    <React.Fragment>
                        <div className="bg-card animate-in fade-in-0 slide-in-from-bottom-1 rounded-xl border p-3 shadow-sm duration-200 motion-reduce:animate-none">
                            <QuestionReferenceMarkdown
                                content={activeQuestion.displayQuestion}
                                references={entry.references}
                                className="text-foreground text-sm font-semibold"
                            />
                        </div>
                        <div className="flex w-full min-w-0 flex-col items-end gap-2">
                            {getQuestionChoiceRows(activeQuestion).map(
                                (choice) => (
                                    <NextActionChoiceBubble
                                        key={choice.value}
                                        disabled={isSubmitting || !canSubmit}
                                        onClick={() => {
                                            void submitNextAction(
                                                choice.value,
                                                replaceReferenceTokensWithNames(
                                                    choice.label,
                                                    entry.references,
                                                ),
                                            );
                                        }}
                                    >
                                        {replaceReferenceTokensWithNames(
                                            choice.label,
                                            entry.references,
                                        )}
                                    </NextActionChoiceBubble>
                                ),
                            )}
                            {activeQuestion.allowOther ? (
                                <button
                                    type="button"
                                    className="text-muted-foreground hover:text-foreground px-2 py-1 text-[13px] disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={isSubmitting || !canSubmit}
                                    onClick={() => selectOther(activeQuestion)}
                                >
                                    Other…
                                </button>
                            ) : null}
                        </div>
                        {activeOtherSelected ? (
                            <div className="w-full min-w-0 space-y-2">
                                <Textarea
                                    value={otherDrafts[activeQuestion.id] ?? ''}
                                    onChange={(event) => {
                                        const value = event.currentTarget.value;
                                        setOtherDrafts((current) => ({
                                            ...current,
                                            [activeQuestion.id]: value,
                                        }));
                                    }}
                                    placeholder={
                                        activeQuestion.placeholder ??
                                        'Tell Phoebe what to do next'
                                    }
                                    disabled={isSubmitting || !canSubmit}
                                    className="min-h-12"
                                />
                                <div className="flex justify-end">
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="bg-training-accent hover:bg-training-accent-strong rounded-full text-white"
                                        disabled={
                                            !activeOtherText ||
                                            isSubmitting ||
                                            !canSubmit
                                        }
                                        onClick={() => {
                                            void submitNextAction(
                                                activeOtherText,
                                                activeOtherText,
                                            );
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <Spinner className="h-4 w-4" />
                                        ) : (
                                            'Send'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </React.Fragment>
                )}
            </div>
        ) : null;

    // Which answers the standard card's receipt shows: the resolved output
    // once it exists, else the locally-built preview from the just-made
    // submission. Compact (Teach Phoebe) resolves through its own trace.
    const receiptAnswers = compact
        ? null
        : resolved
          ? resolvedAnswers
          : showRecordedState
            ? pendingReceiptAnswers
            : null;

    const card = (
        <div
            ref={questionCardRef}
            // Focusable in training mode so arrow keys reach the handler.
            tabIndex={compact ? -1 : undefined}
            data-testid="user-question-card"
            onKeyDown={handleCardKeyDown}
            className={cn(
                'bg-card relative flex flex-col rounded-xl border break-words',
                TEACH_CARD_SHELL_CLASSES,
                // Training mode uses a smaller, full-width box; elsewhere it
                // stays a padded, capped card.
                compact ? 'outline-none' : 'max-w-2xl',
                showRecordedState &&
                    compact &&
                    'user-question-recorded-shell-training w-fit max-w-full rounded-md bg-transparent',
                receiptAnswers
                    ? 'px-3 py-2.5'
                    : showRecordedState
                      ? 'user-question-recorded-shell overflow-hidden p-0'
                      : 'p-3',
                // A receipt (resolved, or previewed right at submit) is a
                // quiet muted card; a pending card stays white with a
                // hairline border. The preview settles in with a short rise.
                receiptAnswers
                    ? cn(
                          'border-border/60 bg-muted/40',
                          showRecordedState && 'user-question-receipt-in',
                      )
                    : showRecordedState
                      ? compact
                          ? 'border-transparent'
                          : 'border-border/70'
                      : resolved
                        ? 'border-border/60 bg-muted/30'
                        : 'border-border animate-in fade-in-0 slide-in-from-bottom-1 shadow-sm duration-200 motion-reduce:animate-none',
            )}
        >
            {receiptAnswers ? (
                // Quiet Q&A receipt: each answered question is a muted
                // caption over its answer, hairline-divided when batched.
                <div className="divide-border/60 divide-y">
                    {receiptAnswers.map((answer) => {
                        const question = questions.find(
                            (item) => item.id === answer.questionId,
                        );
                        return (
                            <div
                                key={answer.questionId}
                                className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0"
                            >
                                <QuestionReferenceMarkdown
                                    content={
                                        question?.displayQuestion ??
                                        answer.question
                                    }
                                    references={entry.references}
                                    className="text-muted-foreground text-xs"
                                />
                                {answer.skipped ? (
                                    <p className="text-muted-foreground text-sm italic">
                                        Skipped
                                    </p>
                                ) : (
                                    <Markdown className="text-foreground text-sm">
                                        {replaceReferenceTokensWithNames(
                                            displayResolvedAnswerText(
                                                answer,
                                                question,
                                            ),
                                            entry.references,
                                        )}
                                    </Markdown>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : showRecordedState ? (
                <TeachPhoebeThankYou
                    showSprout={showTrainingModeContext}
                    variant={compact ? 'chip' : 'card'}
                />
            ) : activeQuestion ? (
                <React.Fragment>
                    {showTrainingModeContext ? (
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-training-accent-strong flex items-center gap-1.5 text-xs font-semibold">
                                <TeachPhoebeSproutIcon className="teach-phoebe-sprout text-training-accent h-3.5 w-3.5 overflow-visible" />
                                Teach Phoebe
                            </p>
                            {questions.length > 1 ? (
                                <span className="text-muted-foreground text-xs font-medium tabular-nums">
                                    {activeQuestionIndex + 1} of{' '}
                                    {questions.length}
                                </span>
                            ) : null}
                        </div>
                    ) : questions.length > 1 ? (
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
                                Phoebe asks
                            </p>
                            <div className="flex shrink-0 items-center gap-2">
                                <div className="flex items-center gap-1.5">
                                    {questions.map((question, index) => (
                                        <button
                                            key={question.id}
                                            type="button"
                                            aria-label={`Go to question ${index + 1} of ${questions.length}`}
                                            aria-current={
                                                index === activeQuestionIndex
                                            }
                                            onClick={() =>
                                                setActiveQuestionIndex(index)
                                            }
                                            className={cn(
                                                'h-1.5 w-1.5 rounded-full transition-colors',
                                                index === activeQuestionIndex
                                                    ? 'bg-primary'
                                                    : 'bg-border hover:bg-muted-foreground/50 cursor-pointer',
                                            )}
                                        />
                                    ))}
                                </div>
                                {onCancel ? (
                                    <Button
                                        type="button"
                                        size="icon"
                                        variant="ghost"
                                        className="text-muted-foreground hover:text-foreground h-6 w-6"
                                        aria-label="Cancel question"
                                        disabled={!canCancel}
                                        onClick={() => {
                                            void cancelQuestion();
                                        }}
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    ) : onCancel ? (
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="text-muted-foreground hover:text-foreground absolute top-2 right-2 h-6 w-6"
                            aria-label="Cancel question"
                            disabled={!canCancel}
                            onClick={() => {
                                void cancelQuestion();
                            }}
                        >
                            <X className="h-3.5 w-3.5" />
                        </Button>
                    ) : null}
                    {compact && questions.length > 1 ? (
                        <div
                            className="bg-training-accent/15 mt-2 mb-3 h-0.5 overflow-hidden rounded-full"
                            aria-hidden={true}
                        >
                            <div
                                className="bg-training-accent h-full rounded-full transition-[width] duration-200 motion-reduce:transition-none"
                                style={{
                                    width: `${((activeQuestionIndex + 1) / questions.length) * 100}%`,
                                }}
                            />
                        </div>
                    ) : null}
                    <React.Fragment>
                        <div
                            className={cn(
                                'max-h-96 overflow-y-auto',
                                compact
                                    ? 'mt-2 space-y-4'
                                    : cn(
                                          'space-y-4',
                                          questions.length > 1 && 'mt-3',
                                      ),
                            )}
                        >
                            {questions.map((question, index) => {
                                const isActive = index === activeQuestionIndex;
                                // One question at a time everywhere; the
                                // keyed wrapper re-mounts on advance so each
                                // question slides in. The dots (standard) or
                                // progress bar (training) navigate a batch.
                                if (!isActive) {
                                    return null;
                                }
                                const draft = draftAnswers[question.id];
                                return (
                                    <div
                                        key={question.id}
                                        ref={activeQuestionRef}
                                        className="teach-question-slide-in space-y-2"
                                    >
                                        <div>
                                            <QuestionReferenceMarkdown
                                                content={
                                                    question.displayQuestion
                                                }
                                                references={entry.references}
                                                className={cn(
                                                    'text-foreground text-sm font-semibold',
                                                    // Keep the first line
                                                    // clear of the corner
                                                    // cancel button.
                                                    !compact &&
                                                        questions.length ===
                                                            1 &&
                                                        !!onCancel &&
                                                        'pr-7',
                                                )}
                                            />
                                            {!compact &&
                                            question.inputType ===
                                                'multi_choice' ? (
                                                <p className="text-muted-foreground mt-0.5 text-xs">
                                                    Select all that apply
                                                </p>
                                            ) : null}
                                        </div>
                                        {question.inputType === 'text' ? (
                                            <Textarea
                                                value={
                                                    typeof draft === 'string'
                                                        ? draft
                                                        : ''
                                                }
                                                onChange={(event) =>
                                                    setAnswer(
                                                        question,
                                                        event.currentTarget
                                                            .value,
                                                    )
                                                }
                                                placeholder={
                                                    question.placeholder ??
                                                    'Type your answer'
                                                }
                                                disabled={
                                                    isSubmitting || !canSubmit
                                                }
                                                className={
                                                    compact
                                                        ? 'min-h-16'
                                                        : 'min-h-24'
                                                }
                                            />
                                        ) : question.searchable ? (
                                            renderSearchableSingleChoice(
                                                question,
                                                {
                                                    muted: false,
                                                    disabled: false,
                                                },
                                            )
                                        ) : (
                                            renderChoiceRows(question, {
                                                muted: false,
                                                disabled: false,
                                                teachStyle: compact,
                                            })
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div
                            className={cn(
                                'mt-3 flex flex-wrap items-center justify-end',
                                compact ? 'gap-2' : 'gap-1',
                            )}
                        >
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className={cn(
                                    !compact &&
                                        'text-muted-foreground hover:text-foreground',
                                )}
                                onClick={() => {
                                    void skipQuestion();
                                }}
                                disabled={isSubmitting || !canSubmit}
                            >
                                Skip
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                className={cn(
                                    'rounded-full',
                                    compact &&
                                        'bg-training-accent hover:bg-training-accent-strong text-white',
                                )}
                                onClick={() => {
                                    void goToNextQuestion();
                                }}
                                disabled={!canAdvance}
                            >
                                {isSubmitting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <React.Fragment>
                                        {primaryQuestionActionLabel(
                                            compact,
                                            activeQuestionIndex >=
                                                questions.length - 1,
                                        )}
                                        {!compact ? (
                                            <CornerDownLeft className="ml-1 h-4 w-4" />
                                        ) : null}
                                    </React.Fragment>
                                )}
                            </Button>
                        </div>
                    </React.Fragment>
                </React.Fragment>
            ) : null}
        </div>
    );

    const renderedCard = nextActionCard ?? card;

    if (nested) {
        return renderedCard;
    }

    if (presentation === 'minimal') {
        return (
            <div className="ml-7 flex w-[calc(100%-1.75rem)] max-w-full min-w-0 justify-start">
                {renderedCard}
            </div>
        );
    }

    return (
        <div className={cn('flex justify-start', TEACH_CARD_SHELL_CLASSES)}>
            {renderedCard}
        </div>
    );
}
